/**
 * ComplianceWizard.js
 * Multi-step dialog that guides admins through Florida electronic-voting
 * compliance checks before creating a new election — FS §718.128 for
 * condominiums (Chapter 718) or FS §720.317 for homeowners' associations
 * (Chapter 720).
 *
 * Steps:
 *  0 – Jurisdiction  (condo / HOA / neither)
 *  1 – Election type  (board vs general)
 *  2 – Compliance checklist
 *  3 – Summary + election title → create
 */
import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Stepper, Step, StepLabel,
  Radio, RadioGroup, FormControlLabel, TextField,
  Chip, Divider, Alert, Paper, LinearProgress, Tooltip,
} from "@mui/material";
import GavelIcon          from "@mui/icons-material/Gavel";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import CancelIcon         from "@mui/icons-material/Cancel";
import WarningAmberIcon   from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon   from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon    from "@mui/icons-material/HelpOutline";
import ArrowBackIcon      from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon   from "@mui/icons-material/ArrowForward";
import AddIcon            from "@mui/icons-material/Add";

// ── Compliance checklist items ────────────────────────────────────────────────
// Subsection citations below were verified directly against the Florida
// Senate's published statute text (flsenate.gov/Laws/Statutes/2025/718.128
// and .../720.317), not carried over from assumption. A few condo (718.128)
// items don't have a clean single-subsection match (e.g. the 14-day board
// meeting notice sits in general condo meeting-notice rules, not §718.128
// itself), so those cite the section as a whole rather than guess a digit.
const CHECKS_BY_CHAPTER = {
  condo: [
    {
      id: "board_resolution",
      label: "Board Resolution Adopted",
      detail: "The board has adopted a resolution authorizing electronic voting and establishing consent/opt-out procedures.",
      statute: "Sec. 718.128(4)",
      critical: true,
    },
    {
      id: "notice_14_days",
      label: "14-Day Notice for Resolution Meeting",
      detail: "Owners received at least 14 days' notice before the board meeting where the resolution was adopted.",
      statute: "FS §718.128",
      critical: true,
    },
    {
      id: "owner_consent",
      label: "Owner Consent Obtained",
      detail: "Each owner who votes electronically has consented (electronically or in writing) to do so. Owners were not auto-enrolled.",
      statute: "Sec. 718.128(4)",
      critical: true,
    },
    {
      id: "secure_platform",
      label: "Secure & Authenticated Platform",
      detail: "The voting platform verifies voter identity, prevents vote alteration during transmission, and provides a receipt/confirmation to the voter.",
      statute: "Sec. 718.128(2)",
      critical: true,
    },
    {
      id: "device_test_14",
      label: "14-Day Device Test Window",
      detail: "Owners are given a way to verify their device can communicate with the voting system at least 14 days before the voting deadline.",
      statute: "Sec. 718.128(1)",
      critical: false,
    },
    {
      id: "record_retention",
      label: "Record Retention & Recount Access",
      detail: "Electronic votes are stored and accessible for recounts, inspections, and review by election officials.",
      statute: "Sec. 718.128(2)",
      critical: false,
    },
    {
      id: "opt_out_procedure",
      label: "Opt-In / Opt-Out Procedures Established",
      detail: "The board resolution includes procedures for owners to opt in and to continue or revoke their consent to vote electronically.",
      statute: "Sec. 718.128(4)",
      critical: false,
    },
  ],
  hoa: [
    {
      id: "board_resolution",
      label: "Board Resolution Adopted",
      detail: "The board has adopted a resolution authorizing online voting and establishing consent/opt-out procedures.",
      statute: "Sec. 720.317(3)",
      critical: true,
    },
    {
      id: "notice_14_days",
      label: "14-Day Notice for Resolution Meeting",
      detail: "Members received at least 14 days' written notice before the board meeting where the resolution was considered, with an affidavit of compliance filed in the official records.",
      statute: "Sec. 720.317(3)",
      critical: true,
    },
    {
      id: "owner_consent",
      label: "Member Consent Obtained",
      detail: "Each member who votes online has consented (electronically or in writing) to do so, and was not auto-enrolled. Consent remains valid until the member opts out.",
      statute: "Sec. 720.317(3)-(4)",
      critical: true,
    },
    {
      id: "secure_platform",
      label: "Secure & Authenticated Platform",
      detail: "The voting platform verifies voter identity, prevents vote alteration during transmission, and provides a receipt/confirmation to the voter.",
      statute: "Sec. 720.317(1)(b)",
      critical: true,
    },
    {
      id: "device_test_14",
      label: "14-Day Device Test Window",
      detail: "Members are given a way to verify their device can communicate with the voting system at least 14 days before the voting deadline.",
      statute: "Sec. 720.317(1)(a)",
      critical: false,
    },
    {
      id: "record_retention",
      label: "Record Retention & Recount Access",
      detail: "Electronic votes are stored and accessible for recounts, inspections, and review by election officials.",
      statute: "Sec. 720.317(1)(b)",
      critical: false,
    },
    {
      id: "opt_out_procedure",
      label: "Opt-In / Opt-Out Procedures Established",
      detail: "The board resolution includes procedures for members to opt in and to continue or revoke their consent to vote online.",
      statute: "Sec. 720.317(3)-(4)",
      critical: false,
    },
  ],
};

const BOARD_ONLY_CHECK_BY_CHAPTER = {
  condo: {
    id: "ballot_secrecy",
    label: "Ballot Secrecy Preserved",
    detail: "For board elections, the system permanently separates voter identity from the ballot so no one can determine how a specific owner voted.",
    statute: "Sec. 718.128(1)-(2)",
    critical: true,
  },
  hoa: {
    id: "ballot_secrecy",
    label: "Ballot Secrecy Preserved",
    detail: "For board elections, the system permanently separates voter identity from the ballot so no one can determine how a specific member voted.",
    statute: "Sec. 720.317(1)(b)",
    critical: true,
  },
};

const CHAPTER_STATUTE = { condo: "FS §718.128", hoa: "FS §720.317" };

// ── Helpers ───────────────────────────────────────────────────────────────────
const STEP_LABELS = ["Jurisdiction", "Election Type", "Compliance Check", "Review & Create"];

const answerColor = (val) => {
  if (val === "yes")     return { color: "#16a34a", bg: "rgba(22,163,74,0.08)" };
  if (val === "no")      return { color: "#dc2626", bg: "rgba(220,38,38,0.07)" };
  return                        { color: "#6b778c", bg: "rgba(107,114,128,0.06)" };
};

// ── Main component ────────────────────────────────────────────────────────────
const ComplianceWizard = ({ open, onClose, onCreate }) => {
  const [step,          setStep]          = useState(0);
  const [jurisdiction,   setJurisdiction]  = useState(null);   // "condo" | "hoa" | "none"
  const [electionType,   setElectionType]  = useState(null);   // "board" | "general"
  const [answers,        setAnswers]       = useState({});     // id → "yes"|"no"
  const [title,          setTitle]         = useState("");
  const [creating,       setCreating]      = useState(false);

  const jurisdictionApplies = jurisdiction === "condo" || jurisdiction === "hoa";
  const baseChecks = jurisdictionApplies ? CHECKS_BY_CHAPTER[jurisdiction] : [];
  const checks = electionType === "board" && jurisdictionApplies
    ? [...baseChecks, BOARD_ONLY_CHECK_BY_CHAPTER[jurisdiction]]
    : baseChecks;

  const answered    = checks.filter(c => answers[c.id]);
  const allAnswered = answered.length === checks.length;
  const failedCritical = checks.filter(c => c.critical && answers[c.id] === "no");
  const warnings       = checks.filter(c => !c.critical && answers[c.id] === "no");
  const passed         = checks.filter(c => answers[c.id] === "yes").length;
  const score          = checks.length ? Math.round((passed / checks.length) * 100) : 0;

  const complianceLevel = failedCritical.length > 0
    ? "non-compliant"
    : warnings.length > 0
    ? "warnings"
    : allAnswered ? "compliant" : "incomplete";

  const LEVEL_CONFIG = {
    "compliant":     { color: "#16a34a", bg: "#f0fdf4", border: "#86efac", label: "Fully Compliant",    icon: <CheckCircleIcon sx={{ color: "#16a34a" }} /> },
    "warnings":      { color: "#b45309", bg: "#fffbeb", border: "#fcd34d", label: "Proceed with Caution", icon: <WarningAmberIcon sx={{ color: "#b45309" }} /> },
    "non-compliant": { color: "#dc2626", bg: "#fef2f2", border: "#fca5a5", label: "Not Compliant",      icon: <CancelIcon sx={{ color: "#dc2626" }} /> },
    "incomplete":    { color: "#6b778c", bg: "#f8fafc", border: "#e2e8f0", label: "Incomplete",         icon: <InfoOutlinedIcon sx={{ color: "#6b778c" }} /> },
  };
  const lvl = LEVEL_CONFIG[complianceLevel];

  const reset = () => {
    setStep(0); setJurisdiction(null); setElectionType(null);
    setAnswers({}); setTitle(""); setCreating(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    if (!title.trim()) return;
    setCreating(true);
    try {
      await onCreate({
        title: title.trim(),
        isBoardElection: electionType === "board",
        // Once a chapter applies, the election is configured to actually
        // comply going forward — not just tracked in a description string.
        complianceChapter: jurisdictionApplies ? jurisdiction : null,
        complianceSummary: {
          jurisdictionApplies,
          jurisdiction,
          electionType,
          score,
          level: complianceLevel,
          failedCritical: failedCritical.map(c => c.id),
          warnings: warnings.map(c => c.id),
          answers,
        },
      });
      reset();
    } finally {
      setCreating(false);
    }
  };

  const canGoNext = () => {
    if (step === 0) return jurisdiction !== null;
    if (step === 1) return electionType !== null;
    if (step === 2) return allAnswered;
    if (step === 3) return title.trim().length > 0;
    return false;
  };

  // If neither condo nor HOA, skip compliance - go straight to title
  const handleNext = () => {
    if (step === 0 && jurisdiction === "none") { setStep(3); return; }
    setStep(s => s + 1);
  };
  const handleBack = () => {
    if (step === 3 && jurisdiction === "none") { setStep(0); return; }
    setStep(s => s - 1);
  };

  // ── Render steps ─────────────────────────────────────────────────────────────
  const renderStep0 = () => (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{ p: 1.25, bgcolor: "rgba(47,99,173,0.08)", borderRadius: "12px" }}>
          <GavelIcon sx={{ color: "#2f63ad", fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>Jurisdiction Check</Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Chapter 718 governs Florida condominiums (FS §718.128); Chapter 720 governs
            homeowners' associations (FS §720.317).
          </Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: "14px", bgcolor: "rgba(47,99,173,0.03)", border: "1px solid rgba(47,99,173,0.10)", mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}>
          What type of association is this?
        </Typography>
        <RadioGroup value={jurisdiction || ""} onChange={e => setJurisdiction(e.target.value)}>
          <FormControlLabel value="condo" control={<Radio />} label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Florida Condominium Association (Chapter 718)</Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>FS §718.128 applies. We'll walk through condominium compliance requirements.</Typography>
            </Box>
          } sx={{ mb: 1, alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }} />
          <FormControlLabel value="hoa" control={<Radio />} label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Florida Homeowners' Association (Chapter 720)</Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>FS §720.317 applies. We'll walk through HOA compliance requirements.</Typography>
            </Box>
          } sx={{ mb: 1, alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }} />
          <FormControlLabel value="none" control={<Radio />} label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Cooperative / Other (Chapter 719 or none of the above)</Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>Neither statute directly applies. We'll skip the checklist.</Typography>
            </Box>
          } sx={{ alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }} />
        </RadioGroup>
      </Paper>

      {jurisdiction === "none" && (
        <Alert severity="info" sx={{ borderRadius: "12px" }}>
          Your association is not subject to FS §718.128 or §720.317. You can still run electronic elections - click Next to name and create your election.
        </Alert>
      )}
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>Election Type</Typography>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
        Board elections have extra secrecy requirements
        {jurisdictionApplies ? ` under ${BOARD_ONLY_CHECK_BY_CHAPTER[jurisdiction].statute}` : ""}.
      </Typography>

      <RadioGroup value={electionType || ""} onChange={e => setElectionType(e.target.value)}>
        {[
          { value: "board",   label: "Board of Directors Election",       desc: "Ballot secrecy required - voter identity permanently separated from ballot." },
          { value: "general", label: "General Vote / Amendment",          desc: "Budget, bylaw amendments, special assessments, recalls, etc." },
        ].map(opt => (
          <Paper key={opt.value} elevation={0} onClick={() => setElectionType(opt.value)}
            sx={{
              p: 2, mb: 1.5, borderRadius: "12px", cursor: "pointer",
              border: `2px solid ${electionType === opt.value ? "#2f63ad" : "rgba(47,99,173,0.10)"}`,
              bgcolor: electionType === opt.value ? "rgba(47,99,173,0.04)" : "#fff",
              transition: "all 0.15s",
            }}>
            <FormControlLabel value={opt.value} control={<Radio />} label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{opt.label}</Typography>
                <Typography variant="caption" sx={{ color: "#6b778c" }}>{opt.desc}</Typography>
              </Box>
            } sx={{ m: 0, alignItems: "flex-start", "& .MuiRadio-root": { mt: 0.25 } }} />
          </Paper>
        ))}
      </RadioGroup>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
          {jurisdictionApplies ? CHAPTER_STATUTE[jurisdiction] : "Compliance"} Checklist
        </Typography>
        <Chip label={`${answered.length} / ${checks.length} answered`} size="small"
          sx={{ fontWeight: 700, bgcolor: "rgba(47,99,173,0.08)", color: "#2f63ad" }} />
      </Box>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
        Answer each item honestly. This determines whether your election is compliant before it's created.
      </Typography>

      {checks.map((check) => {
        const val = answers[check.id];
        const ac  = answerColor(val);
        return (
          <Paper key={check.id} elevation={0} sx={{
            p: 2, mb: 1.5, borderRadius: "12px",
            border: `1.5px solid ${val ? ac.color + "40" : "rgba(47,99,173,0.08)"}`,
            bgcolor: val ? ac.bg : "#fafcff",
          }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.25, alignItems: "flex-start" }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{check.label}</Typography>
                  {check.critical && (
                    <Chip label="Required" size="small" sx={{ fontSize: 10, height: 18, fontWeight: 700, bgcolor: "rgba(220,38,38,0.09)", color: "#dc2626" }} />
                  )}
                  <Chip label={check.statute} size="small" sx={{ fontSize: 10, height: 18, fontWeight: 600, bgcolor: "rgba(47,99,173,0.07)", color: "#2f63ad" }} />
                </Box>
                <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 0.5 }}>
                  {check.detail}
                </Typography>
              </Box>
              <Tooltip title={check.detail}>
                <HelpOutlineIcon sx={{ color: "#b0bec5", fontSize: 18, mt: 0.25, flexShrink: 0 }} />
              </Tooltip>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              {["yes", "no"].map(opt => (
                <Button key={opt} size="small" variant={val === opt ? "contained" : "outlined"}
                  onClick={() => setAnswers(prev => ({ ...prev, [check.id]: opt }))}
                  startIcon={opt === "yes" ? <CheckCircleIcon /> : <CancelIcon />}
                  sx={{
                    borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: 12,
                    boxShadow: "none",
                    ...(val === opt && opt === "yes"  ? { bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } } : {}),
                    ...(val === opt && opt === "no"   ? { bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } } : {}),
                    ...(val !== opt ? {
                      color: opt === "yes" ? "#16a34a" : "#dc2626",
                      borderColor: opt === "yes" ? "rgba(22,163,74,0.3)" : "rgba(220,38,38,0.3)",
                      "&:hover": { borderColor: opt === "yes" ? "#16a34a" : "#dc2626", bgcolor: opt === "yes" ? "rgba(22,163,74,0.04)" : "rgba(220,38,38,0.04)" },
                    } : {}),
                  }}>
                  {opt === "yes" ? "Yes" : "No"}
                </Button>
              ))}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      {/* Compliance summary card */}
      {jurisdictionApplies && (
        <>
          <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: "14px", border: `2px solid ${lvl.border}`, bgcolor: lvl.bg }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              {lvl.icon}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: lvl.color }}>{lvl.label}</Typography>
                <Typography variant="caption" sx={{ color: "#6b778c" }}>
                  {passed} of {checks.length} requirements met · {score}% compliance score
                </Typography>
              </Box>
              <Box sx={{ width: 56, height: 56, position: "relative" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: lvl.color, textAlign: "center", lineHeight: "56px" }}>
                  {score}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress variant="determinate" value={score}
              sx={{ borderRadius: 4, height: 8, bgcolor: "rgba(0,0,0,0.08)", "& .MuiLinearProgress-bar": { bgcolor: lvl.color, borderRadius: 4 } }} />
          </Paper>

          {/* Issues */}
          {failedCritical.length > 0 && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Critical Issues ({failedCritical.length})</Typography>
              {failedCritical.map(c => (
                <Typography key={c.id} variant="caption" sx={{ display: "block" }}>• {c.label} - {c.statute}</Typography>
              ))}
              <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#7f1d1d" }}>
                Proceeding with these unresolved may expose the association to legal challenges.
              </Typography>
            </Alert>
          )}
          {warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Caution ({warnings.length})</Typography>
              {warnings.map(c => (
                <Typography key={c.id} variant="caption" sx={{ display: "block" }}>• {c.label}</Typography>
              ))}
            </Alert>
          )}
          {complianceLevel === "compliant" && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: "12px" }}>
              All {CHAPTER_STATUTE[jurisdiction]} requirements are confirmed. This election can proceed with full statutory compliance.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Title input */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142", mb: 1 }}>
        Name this election
      </Typography>
      <TextField
        fullWidth autoFocus
        label="Election Title"
        placeholder="e.g. 2025 Annual Board Election"
        value={title}
        onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && title.trim()) handleCreate(); }}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        helperText={
          jurisdictionApplies && electionType === "board"
            ? "Board election - ballot secrecy mode will be enforced."
            : jurisdictionApplies
            ? `${CHAPTER_STATUTE[jurisdiction]} compliance mode will be enabled for this election.`
            : ""
        }
      />

      {jurisdictionApplies && (
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`${CHAPTER_STATUTE[jurisdiction]} Tracked`}
            sx={{ fontWeight: 700, bgcolor: "rgba(47,99,173,0.08)", color: "#2f63ad" }} />
          {electionType === "board" && (
            <Chip size="small" label="Board Election - Ballot Secrecy On"
              sx={{ fontWeight: 700, bgcolor: "rgba(22,163,74,0.08)", color: "#16a34a" }} />
          )}
          {complianceLevel === "non-compliant" && (
            <Chip size="small" label="Non-Compliant - Proceed at Own Risk"
              sx={{ fontWeight: 700, bgcolor: "rgba(220,38,38,0.08)", color: "#dc2626" }} />
          )}
        </Box>
      )}
    </Box>
  );

  const STEP_CONTENT = [renderStep0, renderStep1, renderStep2, renderStep3];
  // Skip steps 1 and 2 if neither condo nor HOA (shown as disabled in stepper)
  const visibleSteps = jurisdiction === "none"
    ? ["Jurisdiction", "Review & Create"]
    : STEP_LABELS;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>

      {/* Header */}
      <DialogTitle sx={{ pb: 0, pt: 2.5, px: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ p: 1, bgcolor: "rgba(47,99,173,0.08)", borderRadius: "10px" }}>
            <GavelIcon sx={{ color: "#2f63ad", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142", lineHeight: 1.2 }}>
              New Election Setup
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              {jurisdictionApplies ? `Florida ${CHAPTER_STATUTE[jurisdiction]} Compliance Wizard` : "Florida Statute Compliance Wizard"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={step} alternativeLabel
          sx={{
            "& .MuiStepLabel-label": { fontSize: 11, fontWeight: 600 },
            "& .MuiStepConnector-line": { borderColor: "rgba(47,99,173,0.15)" },
          }}>
          {STEP_LABELS.map((label, i) => {
            const skip = jurisdiction === "none" && (i === 1 || i === 2);
            return (
              <Step key={label} completed={i < step && !skip}>
                <StepLabel
                  optional={skip ? <Typography variant="caption" sx={{ color: "#9eaab5" }}>N/A</Typography> : null}
                  sx={{ "& .MuiStepLabel-label.Mui-disabled": { color: skip ? "#c5cdd6" : undefined } }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {STEP_CONTENT[step]?.()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1, justifyContent: "space-between" }}>
        <Button onClick={step === 0 ? handleClose : handleBack}
          startIcon={step === 0 ? null : <ArrowBackIcon />}
          sx={{ textTransform: "none", fontWeight: 600, color: "#5f6b7a", borderRadius: "10px" }}>
          {step === 0 ? "Cancel" : "Back"}
        </Button>

        {step < 3 ? (
          <Button variant="contained" onClick={handleNext} disabled={!canGoNext()}
            endIcon={<ArrowForwardIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", boxShadow: "none", bgcolor: "#2f63ad", "&:hover": { bgcolor: "#1f4f94", boxShadow: "none" } }}>
            {step === 2 && complianceLevel === "non-compliant" ? "Proceed Anyway" : "Next"}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleCreate}
            disabled={!title.trim() || creating}
            startIcon={<AddIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", boxShadow: "none",
              bgcolor: complianceLevel === "non-compliant" ? "#dc2626" : "#2f63ad",
              "&:hover": { bgcolor: complianceLevel === "non-compliant" ? "#b91c1c" : "#1f4f94", boxShadow: "none" } }}>
            {creating ? "Creating…" : "Create Election"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ComplianceWizard;
